import * as z from 'zod/v4'

import {ClientToolStatusSchema} from '../commands/response'

// Shared chatbot request contract for POST /learn/chatbotThread/:id. Lives here (not in the backend)
// so web can build and type requests against the same source, mirroring how commands are shared.
// Kept lenient (z.object, not strictObject) on the message branches so the contract can grow additive
// client fields without breaking older backends.

// context_ tool payloads. These ride on every user-message request; the backend turns each into a
// context_ tool_call+tool_result in the message chain — on a new thread, or when the payload changes.
// The LLM never calls context_ tools (they aren't declared to it); it's told what they mean in the
// system prompt and reads their injected results.

// context_entryPoint — where in the UI the user launched (and currently is in) the chat.
export const ChatbotEntryPointSchema = z.object({
  location: z.enum(['home', 'note']),
  noteId: z.string().optional()
})
export type ChatbotEntryPoint = z.infer<typeof ChatbotEntryPointSchema>

// context_clientInfo — which client is calling and which client commands it can currently fulfill.
export const ChatbotClientInfoSchema = z.object({
  platform: z.enum(['web', 'iOS', 'android']),
  availableTools: z.array(z.string())
})
export type ChatbotClientInfo = z.infer<typeof ChatbotClientInfoSchema>

// The full context payload the client sends up each user message.
export const ChatbotClientContextSchema = z.object({
  entryPoint: ChatbotEntryPointSchema,
  clientInfo: ChatbotClientInfoSchema
})
export type ChatbotClientContext = z.infer<typeof ChatbotClientContextSchema>

// context_ tool names — never declared to the LLM, only injected into history and described in the
// system prompt. Referenced by both the injection engine and the prompt so the names can't drift.
// Underscore-separated, not slash-separated: both Gemini and Anthropic reject '/' in function names,
// and these values are serialized verbatim as provider function names on the synthetic
// tool_call/tool_result parts injected into the message chain.
export const CONTEXT_TOOL_ENTRY_POINT = 'context_entryPoint'
export const CONTEXT_TOOL_CLIENT_INFO = 'context_clientInfo'

export const MAX_CHATBOT_IMAGE_ATTACHMENTS_PER_MESSAGE = 4

// A user's text message (starts a new turn or a new thread). clientContext is optional so clients
// that don't yet send it still work; the backend applies a stub (home, no available commands) when absent.
// imageAttachmentIds name images already uploaded to POST /learn/chatbotThread/:id/attachments.
export const ChatbotUserMessageSchema = z.object({
  exchangeIndex: z.number().int().min(0),
  role: z.literal('user'),
  text: z.string(),
  imageAttachmentIds: z.array(z.uuid()).max(MAX_CHATBOT_IMAGE_ATTACHMENTS_PER_MESSAGE).optional(),
  clientContext: ChatbotClientContextSchema.optional()
})
export type ChatbotUserMessageBody = z.infer<typeof ChatbotUserMessageSchema>

// A client-side tool result, resuming a suspended turn. Client context is not carried here — it
// rides on user messages only (the UI state can't change mid-turn between a tool call and its ack).
export const ChatbotToolResultSchema = z.object({
  exchangeIndex: z.number().int().min(0),
  role: z.literal('tool_result'),
  toolCallId: z.string(),
  status: ClientToolStatusSchema,
  data: z.record(z.string(), z.unknown()).optional()
})

export const ChatbotRequestBodySchema = z.discriminatedUnion('role', [
  ChatbotUserMessageSchema,
  ChatbotToolResultSchema
])
export type ChatbotRequestBody = z.infer<typeof ChatbotRequestBodySchema>

// Fallback client context for requests that don't yet carry one (older clients / early testing):
// home screen, no commands active. Lets the manifest populate before clients send real context.
// availableTools defaults to none rather than every command — the system prompt tells the model to
// only emit a command present in availableTools, so failing empty means a client that hasn't started
// sending real context yet gets "that's not available," never a command the device can't fulfill.
export function stubChatbotClientContext(): ChatbotClientContext {
  return {
    entryPoint: {location: 'home'},
    clientInfo: {platform: 'web', availableTools: []}
  }
}
